import { FunctionComponent } from "react";
import {
    VALUE_CODES,
    VALUE_LABELS,
    ValueCode
} from "../../models/OverviewDashboard";
import {
    ValueHistoryChartMode,
    ValueHistorySeries
} from "../../models/ValueHistory";
import styles from "./ValueHistoryChart.module.css";

interface ValueHistoryChartProps {
    data: ValueHistorySeries;
    mode: ValueHistoryChartMode;
}

const VALUE_COLORS: Record<ValueCode, string> = {
    bienveillance: "#fed14c",
    engagement: "#2894d2",
    "faire-equipe": "#e71873",
    respect: "#2fac68"
};

const CHART_WIDTH = 900;
const CHART_HEIGHT = 280;
const MARGIN = { top: 18, right: 18, bottom: 44, left: 46 };
const PLOT_WIDTH = CHART_WIDTH - MARGIN.left - MARGIN.right;
const PLOT_HEIGHT = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;
const GRID_VALUES = [0, 25, 50, 75, 100];

const ValueHistoryChart: FunctionComponent<ValueHistoryChartProps> = ({ data, mode }) => {
    if (data.length === 0) {
        return <div className={styles.empty}>Aucune période à afficher.</div>;
    }

    const xForLineIndex = (index: number): number => data.length === 1
        ? MARGIN.left + PLOT_WIDTH / 2
        : MARGIN.left + (index / (data.length - 1)) * PLOT_WIDTH;
    const xForBarIndex = (index: number): number =>
        MARGIN.left + ((index + 0.5) / data.length) * PLOT_WIDTH;
    const xForLabelIndex = mode === "bar" ? xForBarIndex : xForLineIndex;
    const yForValue = (value: number): number => MARGIN.top + ((100 - value) / 100) * PLOT_HEIGHT;

    return (
        <div className={styles.chartContainer}>
            <svg
                className={styles.chart}
                viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
                role="img"
                aria-label="Historique mensuel des valeurs"
            >
                {GRID_VALUES.map((value) => {
                    const y = yForValue(value);
                    return (
                        <g key={value}>
                            <line
                                x1={MARGIN.left}
                                y1={y}
                                x2={CHART_WIDTH - MARGIN.right}
                                y2={y}
                                className={styles.gridLine}
                            />
                            <text
                                x={MARGIN.left - 10}
                                y={y + 4}
                                textAnchor="end"
                                className={styles.axisLabel}
                            >
                                {value}
                            </text>
                        </g>
                    );
                })}

                {data.map((point, index) => (
                    <text
                        key={point.periodStart}
                        x={xForLabelIndex(index)}
                        y={CHART_HEIGHT - 14}
                        textAnchor="middle"
                        className={styles.monthLabel}
                    >
                        {point.label}
                    </text>
                ))}

                {mode === "line"
                    ? VALUE_CODES.map((code) => (
                        <LineSeries
                            key={code}
                            code={code}
                            data={data}
                            xForIndex={xForLineIndex}
                            yForValue={yForValue}
                        />
                    ))
                    : data.map((point, monthIndex) => (
                        <BarGroup
                            key={point.periodStart}
                            point={point}
                            monthIndex={monthIndex}
                            monthCount={data.length}
                            xForIndex={xForBarIndex}
                            yForValue={yForValue}
                        />
                    ))}
            </svg>

            <div className={styles.legend}>
                {VALUE_CODES.map((code) => (
                    <span key={code}>
                        <i style={{ backgroundColor: VALUE_COLORS[code] }} />
                        {VALUE_LABELS[code]}
                    </span>
                ))}
            </div>
        </div>
    );
};

const LineSeries: FunctionComponent<{
    code: ValueCode;
    data: ValueHistorySeries;
    xForIndex: (index: number) => number;
    yForValue: (value: number) => number;
}> = ({ code, data, xForIndex, yForValue }) => {
    const segments = buildLineSegments(data, code, xForIndex, yForValue);

    return (
        <g>
            {segments.map((points, index) => (
                <polyline
                    key={`${code}-${index}`}
                    points={points}
                    fill="none"
                    stroke={VALUE_COLORS[code]}
                    strokeWidth="3"
                    strokeLinejoin="round"
                    strokeLinecap="round"
                />
            ))}
            {data.map((point, index) => {
                const value = point.values[code];
                if (value === undefined) return null;

                return (
                    <circle
                        key={`${code}-${point.periodStart}`}
                        cx={xForIndex(index)}
                        cy={yForValue(value)}
                        r="4"
                        fill="#ffffff"
                        stroke={VALUE_COLORS[code]}
                        strokeWidth="3"
                    >
                        <title>{`${VALUE_LABELS[code]} — ${point.label} : ${value}%`}</title>
                    </circle>
                );
            })}
        </g>
    );
};

const BarGroup: FunctionComponent<{
    point: ValueHistorySeries[number];
    monthIndex: number;
    monthCount: number;
    xForIndex: (index: number) => number;
    yForValue: (value: number) => number;
}> = ({ point, monthIndex, monthCount, xForIndex, yForValue }) => {
    const spacing = PLOT_WIDTH / Math.max(monthCount, 1);
    const groupWidth = Math.min(72, spacing * 0.58);
    const gap = 3;
    const barWidth = Math.max(5, (groupWidth - gap * (VALUE_CODES.length - 1)) / VALUE_CODES.length);
    const totalWidth = barWidth * VALUE_CODES.length + gap * (VALUE_CODES.length - 1);
    const groupStart = xForIndex(monthIndex) - totalWidth / 2;
    const baseline = yForValue(0);

    return (
        <g>
            {VALUE_CODES.map((code, valueIndex) => {
                const value = point.values[code];
                if (value === undefined) return null;
                const y = yForValue(value);

                return (
                    <rect
                        key={code}
                        x={groupStart + valueIndex * (barWidth + gap)}
                        y={y}
                        width={barWidth}
                        height={baseline - y}
                        rx="2"
                        fill={VALUE_COLORS[code]}
                    >
                        <title>{`${VALUE_LABELS[code]} — ${point.label} : ${value}%`}</title>
                    </rect>
                );
            })}
        </g>
    );
};

const buildLineSegments = (
    data: ValueHistorySeries,
    code: ValueCode,
    xForIndex: (index: number) => number,
    yForValue: (value: number) => number
): string[] => {
    const segments: string[] = [];
    let current: string[] = [];

    data.forEach((point, index) => {
        const value = point.values[code];
        if (value === undefined) {
            if (current.length > 0) segments.push(current.join(" "));
            current = [];
            return;
        }

        current.push(`${xForIndex(index)},${yForValue(value)}`);
    });

    if (current.length > 0) segments.push(current.join(" "));
    return segments;
};

export default ValueHistoryChart;
